import { TimelineEntry, TimelineStatus } from '~/app/pages/Transfers/_components/Timeline';
import { buildTransferParty } from '~/app/pages/Transfers/_utils/timeline';
import { ROUTE } from '~/app/routes';
import { Counterparty } from '~/repositories';
import { AccountNumber, BankAccount } from '~/repositories/BankAccountRepository';
import { WireDrawdownRequest } from '~/typings/API';

const wireDrawdownEvents = [
  {
    label: 'Request Sent',
    field: 'sentAt',
  },
  {
    label: 'Request Received',
    field: 'receivedAt',
  },
  {
    label: 'Request Approved',
    field: 'approvedAt',
    status: TimelineStatus.Success,
  },
  {
    label: 'Wire Created',
    field: 'completedAt',
    status: TimelineStatus.Success,
  },
  {
    label: 'Request Rejected',
    field: 'rejectedAt',
    status: TimelineStatus.Danger,
  },
];

export const buildWireDrawdownEvents = ({
  transfer,
  account,
  counterparty,
}: {
  transfer: WireDrawdownRequest;
  account: {
    accountNumber: AccountNumber;
    bankAccount: BankAccount;
    entityType?: 'person' | 'business';
  };
  counterparty: Counterparty;
}): TimelineEntry[] => {
  const rejected = transfer.status === 'rejected';
  const approved = transfer.status === 'approved';

  // Add a "Request Sent" event for sender party data if no sentAt timestamp exists but we have other events
  const events: TimelineEntry[] = [];

  const hasOtherTimestampEvents =
    transfer.receivedAt || transfer.approvedAt || transfer.completedAt || transfer.rejectedAt;

  if (!(transfer as any).sentAt && !transfer.receivedAt && hasOtherTimestampEvents && transfer.bankAccountId) {
    // If the synthetic event is being added because of rejectedAt or completedAt, mark as Success
    const isSuccess = Boolean(transfer.rejectedAt || transfer.completedAt);
    events.push({
      label: 'Request Sent',
      amount: transfer.amount
        ? {
            value: typeof transfer.amount === 'string' ? parseFloat(transfer.amount) : transfer.amount,
            isAdding: false,
            currencyCode: transfer.currencyCode || 'USD',
          }
        : undefined,
      date: undefined,
      partyData: buildTransferParty({
        account,
        counterparty,
        isIncoming: transfer.isIncoming,
        isOriginator: true,
      }),
      status: isSuccess ? TimelineStatus.Success : TimelineStatus.Pending,
      link: undefined,
    });
  }

  const timelineEvents = wireDrawdownEvents
    .filter(
      (event) =>
        (event.field === 'sentAt' && !!(transfer as any)[event.field]) ||
        (event.field === 'receivedAt' && !!transfer[event.field as keyof typeof transfer]) ||
        (event.field === 'rejectedAt' && !!transfer[event.field as keyof typeof transfer]) ||
        (!rejected && event.field === 'completedAt') ||
        (event.field === 'completedAt' && transfer.wireTransferId) ||
        (event.field !== 'rejectedAt' && event.field !== 'sentAt' && !!transfer[event.field as keyof typeof transfer])
    )
    .map((event) => {
      let amount;
      if (transfer.amount) {
        amount = {
          value: typeof transfer.amount === 'string' ? parseFloat(transfer.amount) : transfer.amount,
          isAdding: event.field === 'completedAt',
          currencyCode: transfer.currencyCode || 'USD',
        };
      }

      const date = (transfer[event.field as keyof typeof transfer] as string | undefined)
        ? new Date(transfer[event.field as keyof typeof transfer] as string)
        : undefined;

      let partyData;
      if (['sentAt', 'receivedAt'].includes(event.field)) {
        partyData = buildTransferParty({
          account,
          counterparty,
          isIncoming: transfer.isIncoming,
          isOriginator: true,
        });
      } else if (event.field === 'approvedAt' && approved) {
        partyData = buildTransferParty({
          account,
          counterparty,
          isIncoming: transfer.isIncoming,
          isOriginator: false,
        });
      } else if (event.field === 'completedAt' && !approved && !transfer.isIncoming) {
        partyData = buildTransferParty({
          account,
          counterparty,
          isIncoming: transfer.isIncoming,
          isOriginator: false,
        });
      } else if (event.field === 'rejectedAt' && rejected) {
        partyData = buildTransferParty({
          account,
          counterparty,
          isIncoming: transfer.isIncoming,
          isOriginator: false,
        });
      }

      const defaultStatus = rejected ? TimelineStatus.Danger : TimelineStatus.Pending;
      let status = !!transfer[event.field as keyof typeof transfer] ? event.status : defaultStatus;

      // Ensure "Wire Created" has success status when there's a wireTransferId
      if (event.field === 'completedAt' && transfer.wireTransferId) {
        status = TimelineStatus.Success;
      }

      let link;
      if (event.field === 'completedAt' && transfer.wireTransferId) {
        link = {
          label: 'View transfer',
          path: `${ROUTE.TRANSFERS}/edit/wire/${transfer.wireTransferId}`,
        };
      }

      // Use "Completed" label for outgoing wire drawdowns
      let label = event.label;
      if (event.field === 'completedAt' && !transfer.isIncoming) {
        label = 'Completed';
      }

      return {
        label,
        amount,
        date,
        partyData,
        status,
        link,
      };
    });

  // Add synthetic "Request Approved" event for incoming drawdowns with receivedAt but no approvedAt
  const finalEvents = [...events, ...timelineEvents];

  if (transfer.isIncoming && transfer.receivedAt && !transfer.approvedAt) {
    // Insert the synthetic "Request Approved" event before the "Wire Created" event
    const wireCreatedIndex = finalEvents.findIndex((event) => event.label === 'Wire Created');
    if (wireCreatedIndex !== -1) {
      const syntheticEvent = {
        label: 'Request Approved',
        amount: transfer.amount
          ? {
              value: typeof transfer.amount === 'string' ? parseFloat(transfer.amount) : transfer.amount,
              isAdding: true,
              currencyCode: transfer.currencyCode || 'USD',
            }
          : undefined,
        date: undefined,
        partyData: buildTransferParty({
          account,
          counterparty,
          isIncoming: transfer.isIncoming,
          isOriginator: false,
        }),
        status: TimelineStatus.Pending,
        link: transfer.wireTransferId
          ? {
              label: 'View transfer',
              path: `${ROUTE.TRANSFERS}/edit/wire/${transfer.wireTransferId}`,
            }
          : undefined,
      };
      finalEvents.splice(wireCreatedIndex, 0, syntheticEvent);
    }
  }

  // Add "Wire Initiated" event for outgoing drawdowns with completedAt
  if (!transfer.isIncoming && transfer.completedAt) {
    finalEvents.push({
      label: 'Wire Initiated',
      amount: transfer.amount
        ? {
            value: typeof transfer.amount === 'string' ? parseFloat(transfer.amount) : transfer.amount,
            isAdding: true,
            currencyCode: transfer.currencyCode || 'USD',
          }
        : undefined,
      date: undefined,
      partyData: undefined,
      status: TimelineStatus.Success,
      link: transfer.wireTransferId
        ? {
            label: 'View transfer',
            path: `${ROUTE.TRANSFERS}/edit/wire/${transfer.wireTransferId}`,
          }
        : undefined,
    });
  }

  // Add "Request Initiated" event for outgoing drawdowns with initiated status
  if (!transfer.isIncoming && transfer.status === 'initiated') {
    finalEvents.unshift({
      label: 'Request Initiated',
      amount: transfer.amount
        ? {
            value: typeof transfer.amount === 'string' ? parseFloat(transfer.amount) : transfer.amount,
            isAdding: false,
            currencyCode: transfer.currencyCode || 'USD',
          }
        : undefined,
      date: undefined,
      partyData: buildTransferParty({
        account,
        counterparty,
        isIncoming: transfer.isIncoming,
        isOriginator: true,
      }),
      status: TimelineStatus.Success,
      link: undefined,
    });
  }

  return finalEvents;
};
